<script lang="ts">
    import { BarChart } from 'layerchart';
    import * as Card from '@/components/ui/card';
    import { ChartContainer, type ChartConfig } from '@/components/ui/chart';
    import { TrendingUp, TrendingDown, Minus } from 'lucide-svelte';
    import { cn } from '@/lib/utils';

    type MonthlyData = {
        month: string;
        fullMonth: string;
        count: number;
        value: number;
    };

    type PeriodData = {
        count: number;
        value: number;
        previousCount: number;
        previousValue: number;
        countChange: number;
        valueChange: number;
        data: MonthlyData[];
    };

    type Props = {
        title: string;
        description?: string;
        data: {
            monthly: MonthlyData[];
            periods: Record<string, PeriodData>;
        };
        color: 'green' | 'blue' | 'gray' | 'purple';
        selectedPeriod: 'monthly' | '3month' | '6month' | 'yearly';
        monthlyTarget?: number;
        showTargetProgress?: boolean;
    };

    let { title, description, data, color, selectedPeriod, monthlyTarget = 0, showTargetProgress = false }: Props = $props();

    // Get the current period data (must be first as others depend on it)
    let periodData = $derived(data.periods[selectedPeriod]);
    let chartData = $derived(periodData?.data ?? []);

    // Calculate the target for the selected period
    let periodTarget = $derived.by(() => {
        if (!monthlyTarget || monthlyTarget <= 0) return 0;
        switch (selectedPeriod) {
            case 'monthly':
                return monthlyTarget;
            case '3month':
                return monthlyTarget * 3;
            case '6month':
                return monthlyTarget * 6;
            case 'yearly':
                return monthlyTarget * 12;
            default:
                return monthlyTarget;
        }
    });

    // Calculate percentage of target achieved
    let percentOfTarget = $derived.by(() => {
        if (periodTarget <= 0) return null;
        const value = periodData?.value ?? 0;
        return Math.round((value / periodTarget) * 100);
    });

    // Chart configuration using existing event colors
    let chartConfig: ChartConfig = $derived({
        value: {
            label: 'Value',
            color: `var(--event-${color}-scheduler)`,
        },
    });

    // Format currency
    function formatCurrency(value: number): string {
        return new Intl.NumberFormat('en-GB', {
            style: 'currency',
            currency: 'GBP',
            minimumFractionDigits: 0,
            maximumFractionDigits: 0,
        }).format(value);
    }

    // Format percentage change
    function formatChange(change: number): string {
        const sign = change > 0 ? '+' : '';
        return `${sign}${change}%`;
    }

    // Get trend icon and color
    function getTrendInfo(change: number, isPositiveGood: boolean = true): { icon: typeof TrendingUp; class: string } {
        if (change > 0) {
            return { icon: TrendingUp, class: isPositiveGood ? 'text-green-600' : 'text-red-600' };
        } else if (change < 0) {
            return { icon: TrendingDown, class: isPositiveGood ? 'text-red-600' : 'text-green-600' };
        }
        return { icon: Minus, class: 'text-muted-foreground' };
    }

    // For closed_lost, down is good
    let isPositiveGood = $derived(title !== 'Closed Lost');
    let countTrend = $derived(getTrendInfo(periodData?.countChange ?? 0, isPositiveGood));
    let valueTrend = $derived(getTrendInfo(periodData?.valueChange ?? 0, isPositiveGood));

    // Get the CSS variable for the bar color
    let barColor = $derived(`var(--event-${color}-scheduler)`);
</script>

<Card.Root class="flex flex-col">
    <Card.Header class="pb-2">
        <Card.Title class="text-base font-medium">{title}</Card.Title>
        {#if description}
            <Card.Description>{description}</Card.Description>
        {/if}
    </Card.Header>
    <Card.Content class="flex-1">
        <!-- Stats Summary -->
        <div class="mb-4 grid grid-cols-2 gap-4">
            <div>
                <p class="text-xs text-muted-foreground">Total Value</p>
                <p class="text-xl font-bold">{formatCurrency(periodData?.value ?? 0)}</p>
                <div class="flex items-center gap-1 text-xs">
                    {#if valueTrend.icon === TrendingUp}
                        <TrendingUp class={cn('h-3 w-3', valueTrend.class)} />
                    {:else if valueTrend.icon === TrendingDown}
                        <TrendingDown class={cn('h-3 w-3', valueTrend.class)} />
                    {:else}
                        <Minus class={cn('h-3 w-3', valueTrend.class)} />
                    {/if}
                    <span class={valueTrend.class}>{formatChange(periodData?.valueChange ?? 0)}</span>
                    <span class="text-muted-foreground">vs prev</span>
                </div>
            </div>
            <div>
                <p class="text-xs text-muted-foreground">Lead Count</p>
                <p class="text-xl font-bold">{periodData?.count ?? 0}</p>
                <div class="flex items-center gap-1 text-xs">
                    {#if countTrend.icon === TrendingUp}
                        <TrendingUp class={cn('h-3 w-3', countTrend.class)} />
                    {:else if countTrend.icon === TrendingDown}
                        <TrendingDown class={cn('h-3 w-3', countTrend.class)} />
                    {:else}
                        <Minus class={cn('h-3 w-3', countTrend.class)} />
                    {/if}
                    <span class={countTrend.class}>{formatChange(periodData?.countChange ?? 0)}</span>
                    <span class="text-muted-foreground">vs prev</span>
                </div>
            </div>
        </div>

        <!-- Target Progress -->
        <div class="mb-4 rounded-lg bg-muted/50 p-3">
            <div class="mb-1 flex items-center justify-between text-xs">
                <span class="text-muted-foreground">Target Progress</span>
                {#if showTargetProgress && percentOfTarget !== null}
                    <span class="font-medium">{percentOfTarget}% of {formatCurrency(periodTarget)}</span>
                {:else}
                    <span class="text-muted-foreground">—</span>
                {/if}
            </div>
            <div class="h-2 w-full overflow-hidden rounded-full bg-muted">
                {#if showTargetProgress && percentOfTarget !== null}
                    <div
                        class="h-full transition-all duration-300"
                        style="width: {Math.min(percentOfTarget ?? 0, 100)}%; background-color: {barColor}"
                    ></div>
                {/if}
            </div>
        </div>

        <!-- Bar Chart -->
        {#if chartData.length > 0}
            <div class="overflow-hidden">
                <ChartContainer config={chartConfig} class="h-[180px] w-full">
                    <BarChart
                        data={chartData}
                        x="month"
                        y="value"
                        padding={{ left: 48, right: 8, top: 8, bottom: 24 }}
                        props={{
                            bars: {
                                fill: barColor,
                                radius: 4,
                            },
                        }}
                        tooltip={{
                            formatValue: (d) => formatCurrency(d.value),
                        }}
                    />
                </ChartContainer>
            </div>
        {:else}
            <div class="flex h-[180px] items-center justify-center text-sm text-muted-foreground">
                No data for this period
            </div>
        {/if}
    </Card.Content>
</Card.Root>